<template>
  <div>
    <LoadingBlock :loading="fetchingPaymentProfile" />
    <template v-if="!fetchingPaymentProfile">
      <PaymentProfileForm
        v-if="verificationAllowed"
        v-model="record"
        :sid="sid"
      />

      <template v-else>
        <Info v-if="showInfo" v-model="record" />
        <el-result :title="resultTitle">
          <template #icon>
            <el-image :src="resultIcon" />
          </template>
          <template #subTitle>
            <p v-html="resultText" />
          </template>
          <template #extra>
            <router-link
              to="/payment"
              class="el-button el-button--default el-button--medium"
            >
              Back to payments
            </router-link>
          </template>
        </el-result>
      </template>
    </template>
  </div>
</template>
<script lang="ts">
import { defineComponent, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/modules/user'
import hourglass from '@/apps/traforama/assets/icons/hourglass.png'
import verified from '@/apps/traforama/assets/icons/verified.png'
import LoadingBlock from '@/components/LoadingBlock.vue'
import PaymentProfileForm from './form.vue'
import Info from './Info.vue'
import useFetchPaymentProfile from '@/hooks/useFetchPaymentVerification'

const WAITING_TITLE = 'Your information is currently being processed.'
const APPROVED_TITLE =
  'We are pleased to inform you that your KYC process has been successfully completed.'

const WAITING_TEXT =
  'This process typically takes up to 12 hours to complete.<br>If have any questions or concerns regarding your ' +
  'application, please do not hesitate to reach out to our support team via Live Chat or email ' +
  'at <a href="mailto:support@traforama.com" class="el-link el-link--default is-underline">support@traforama.com</a>'

const APPROVED_TEXT =
  'As a result, you are now eligible to make payments using PayPal. Please be advised that during payment ' +
  'processing, the KYC data you provided will be cross-referenced with the data of the PayPal account owner. ' +
  'Consequently, it is possible that the first payment you make may be pending for up to 24 hours.'

const UNFINISHED_VERIFICATION_STATUSES = ['new', 'resubmission_requested']

export default defineComponent({
  name: 'PaymentProfile',
  components: {
    PaymentProfileForm,
    Info,
    LoadingBlock
  },
  props: {
    sid: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const userStore = useUserStore()
    const { record, fetchingPaymentProfile, fetchPaymentProfile } =
      useFetchPaymentProfile()

    fetchPaymentProfile(props.sid).then((profile) => {
      if (profile.message) {
        ElMessage.error({
          message: profile.message,
          type: 'error',
          duration: 5000,
          dangerouslyUseHTMLString: true
        })
      }
    })
    const showInfo = computed(() => {
      return !userStore.verifyPaymentData || record.value.verified
    })

    const resultIcon = computed(() => {
      return showInfo.value ? verified : hourglass
    })

    const resultTitle = computed(() => {
      return showInfo.value ? APPROVED_TITLE : WAITING_TITLE
    })

    const resultText = computed(() => {
      return showInfo.value ? APPROVED_TEXT : WAITING_TEXT
    })

    const verificationAllowed = computed(() => {
      if (!record.value.id) return true
      if (!userStore.verifyPaymentData || record.value.blocked) return false

      return (
        !record.value.status ||
        Object.values(UNFINISHED_VERIFICATION_STATUSES).includes(
          record.value.status
        )
      )
    })

    return {
      record,
      fetchingPaymentProfile,
      showInfo,
      resultIcon,
      resultTitle,
      resultText,
      verificationAllowed
    }
  }
})
</script>
